import * as day from 'dayjs';
import {
  FlexTimeActivityInstance,
  ICheckinReason,
  IConsequence,
  IConsequenceType,
  IHallPassType,
  IPbisType,
} from 'libs/domain';
import { hall_pass_pb } from 'libs/generated-grpc-web';

import { OptionItem } from '@app/src/app/shared/components/form/components/form-grouped-select/form-grouped-select.types';
import { RegisteredStatus } from '@app/src/app/shared/services/flex-time';

import { BEHAVIOR_FIELDS, CHECK_IN_FIELDS, FORM_FIELDS, HALLPASS_FIELDS } from '../constants/tt-my-class.constants';

export enum AssignmentType {
  HALLPASS = 'hallpass',
  BEHAVIOR = 'behavior',
  CONSEQUENCE = 'consequence',
  CHECK_IN = 'check_in',
  COMMUNICATION = 'communication',
  FLEX_TIME = 'flex_time',
}

export enum FlexAssignmentType {
  ASSIGN = 1,
  REGISTER = 2,
  CHECKIN = 3,
  REGISTER_HOMEROOM = 4,
}

export enum CategoryType {
  HALLPASS = 'hallpass',
  GUIDANCE = 'guidance',
  PRAISE = 'praise',
  CONSEQUENCE = 'consequence',
  MOST_USED = 'most_used',
  MOST_USED_BY_LIST = 'most_used_by_list',
  COMMUNICATION = 'communication',
  FLEX_TIME = 'flex_time',
  CHECK_IN = 'check_in',
}

export enum SettingsMenu {
  VISIBILITY_SETTINGS = 'visibilitySettings',
  NOTIFICATIONS_SETTINGS = 'notificationsSettings',
  OTHER_SETTINGS_MENU = 'otherSettingsMenu',
}

export type ActionCategory = {
  label: string;
  value: CategoryType;
  icon: string;
  id: string; // used for analytics
  iconNamespace?: string;
  hideFromSearch?: boolean;
};

export type ActionItem = {
  value: number;
  label: string;
  color: string;
  icon: string;
  count: number;
  iconNamespace?: string;
  category: ActionCategory;
  assignmentType: AssignmentType;
  data: IConsequenceType | IHallPassType | IPbisType | ICheckinReason | null;
  disabled?: boolean;
  tooltip?: string;
};

export type ActionGroup = ActionCategory & {
  items: Array<Omit<ActionItem, 'category'>>;
};

export type Assignment = {
  value: number;
  assignmentType: AssignmentType;
};

export type HallPassFormData = {
  [HALLPASS_FIELDS.NOTE]: string;
  [HALLPASS_FIELDS.DURATION]: number;
  [HALLPASS_FIELDS.DATE]: day.Dayjs;
  [HALLPASS_FIELDS.TIME]: string;
  [HALLPASS_FIELDS.APPROVED_BY]?: {
    hash: string;
    name: string;
  };
};

export type BehaviorFormData = {
  [BEHAVIOR_FIELDS.NOTE]: string;
};

export type ConsequenceFormData = {
  [HALLPASS_FIELDS.NOTE]: string;
  [HALLPASS_FIELDS.DATE]: day.Dayjs;
};

export type CheckInFormData = {
  [CHECK_IN_FIELDS.NOTE]: string;
};

export type FormData =
  | {
      [FORM_FIELDS.CATEGORY]: CategoryType;
      [FORM_FIELDS.SELECTED_ACTION]: ActionItem;
      [FORM_FIELDS.SELECTED_STUDENTS]: Array<Student>;
      [FORM_FIELDS.LIST_FILTER]: OptionItem[];
      [FORM_FIELDS.SORT_COLUMN]: MyClassSortColumns;
      [FORM_FIELDS.HIDE_PICTURE_FILTER]: boolean;
      [FORM_FIELDS.SEARCH_TEXT_FILTER]: string;
    } & (
      | {
          [AssignmentType.HALLPASS]: HallPassFormData;
        }
      | {
          [AssignmentType.BEHAVIOR]: BehaviorFormData;
        }
      | {
          [AssignmentType.CONSEQUENCE]: ConsequenceFormData;
        }
      | {
          [AssignmentType.CHECK_IN]: CheckInFormData;
        }
    );

export type FormState = 'idle' | 'loading' | 'error' | 'submitting' | 'randomizing';

export type Student = {
  firstName: string;
  lastName: string;
  profileImageUrl: string;
  personHash: string;
  filename: string;
  listTypeOrigin: 'flextime' | 'userlist';
  studentId?: string;
  // student flex data for currently selected flex list
  flexTimeActivity?: {
    instance: FlexTimeActivityInstance;
    status: RegisteredStatus;
    checkedInAt?: Date;
  };
};

export type AssignmentResponse = {
  successHashes: string[];
  data?: IConsequence[] | hall_pass_pb.HallPassWithType.AsObject[] | void;
  successMessage?: string;
};

export type AssignmentHandler = (data: FormData, context?: AssignmentContext) => Promise<AssignmentResponse>;
// some additional context for the assignment
export type AssignmentContext = {
  isTardyCheckIn?: boolean;
};

export type PastAssignment = ActionItem & { assignedAt: Date };

export type MyClassSortColumns =
  | 'firstName'
  | 'lastName'
  | 'checkinPill'
  | 'flextimePill'
  | 'flextimeName'
  | 'praisePill'
  | 'guidancePill';
